import { APIS, SERVICES } from 'src/config/constants'
import { request } from 'src/utils/requestV2'

// ========================
// Enums
// ========================
export enum ConversationScope {
  Room = 'room',
  Admin = 'admin',
  Internal = 'internal',
  Direct = 'direct',
}

export enum ParticipantType {
  Family = 'family',
  Staff = 'staff',
}

// ========================
// Core Models
// ========================
export interface MessageModel {
  id: string
  conversationId: string
  senderUserId: string
  senderActingAsGroupId: string | null
  senderActingAsGroupName: string | null
  content: string
  timestamp: string
  isRead: boolean
  readAt: string | null
  editedAt: string | null
  broadcastMessageId: string | null
  isOwnMessage: boolean
  isUrgent: boolean
  isLegacy: boolean
  displayName: string
}

export interface ConversationModel {
  conversationId: string
  scope: ConversationScope
  memberId: string | null
  familyId: string | null
  groupId: string | null
  groupName: string | null
  targetGroupId: string | null
  participantName: string
  messages: MessageModel[]
  continuationToken: string | null
}

export interface InboxItemModel {
  id: string
  conversationId: string
  partnerName: string
  partnerAvatar: string | null
  lastMessage: string | null
  lastMessageTime: string
  lastMessageSenderName: string | null
  lastMessageSenderId: string | null
  unreadCount: number
  scope: ConversationScope
  participantType: string
  isTemporary: boolean
  markedUnread: boolean
  isBroadcastSent: boolean
  broadcastMessageId: string | null
  broadcastRecipientsPreview: string | null
  broadcastRecipientsCount: number | null
  familyId: string | null
  isUrgent: boolean
  urgentAcknowledged: boolean
}

export interface BroadcastRecipientSnapshot {
  memberId: string
  memberName: string
  familyId: string
  familyName: string
  conversationId: string
  email: string | null
}

export interface BroadcastMessageFilter {
  includedGroupIds: string[] | null
  studentStatuses: string[] | null
  memberIds: string[] | null
}

export interface BroadcastFailedRecipient {
  memberId: string
  memberName: string
}

export interface BroadcastMessageModel {
  id: string
  broadcastConversationId: string
  content: string
  sentAt: string
  senderDisplayName: string
  senderActingAsGroupName?: string
  recipients: BroadcastRecipientSnapshot[]
  filterCriteria: BroadcastMessageFilter | null
  scope: ConversationScope
  totalBatches: number
  completedBatches: number
  deliveryFailedCount: number
  deliveryFailedRecipients: BroadcastFailedRecipient[]
}

export interface BroadcastConversationModel {
  broadcastConversationId: string
  scope: ConversationScope
  messages: BroadcastMessageModel[]
  recipients: BroadcastRecipientSnapshot[]
}

export interface AnnouncementModel {
  id: string
  title: string
  content: string
  senderDisplayName: string
  senderUserId?: string
  senderRole?: string
  publishedAt: string
  expiresAt?: string
  isPinned: boolean
  targetRoles?: string[]
  targetGroupIds?: string[]
  isRead?: boolean
}

export interface GroupModel {
  groupId: string
  name: string
  familyCount: number
  memberCount: number
}

// ========================
// Request Models
// ========================
export interface SendMessageRequest {
  conversationId: string
  content: string
  actingAsGroupId?: string
  isUrgent?: boolean
}

export interface SendBroadcastMessageRequest {
  content: string
  memberIds: string[]
  includedGroupIds?: string[]
  studentStatuses?: string[]
  scope?: ConversationScope
  actingAsGroupId?: string
}

export interface StartDirectConversationRequest {
  targetUserId: string
  content: string
  isUrgent?: boolean
}

export interface EditMessageRequest {
  content: string
}

export interface SendAnnouncementRequest {
  title: string
  content: string
  targetGroupIds?: string[]
  targetStudentStatuses?: string[]
  targetRoles: string[]
  expiresAt?: string
  isPinned?: boolean
}

export interface EditAnnouncementRequest {
  title: string
  content: string
  targetRoles: string[]
  targetGroupIds?: string[]
  targetStudentStatuses?: string[]
  expiresAt?: string
  isPinned?: boolean
}

export interface SearchMessagesParams {
  searchText?: string
  fromDate?: Date
  toDate?: Date
  pageSize?: number
  continuationToken?: string
}

// ========================
// Response Models
// ========================
export interface InboxResponse {
  items: InboxItemModel[]
  totalUnreadCount: number
  unreadByScope: Record<string, number>
}

export interface AnnouncementsResponse {
  items: AnnouncementModel[]
  pinnedItems?: AnnouncementModel[]
  continuationToken?: string
  asOf?: string
}

export interface UnreadCountsResponse {
  total: number
  roomThreads: number
  adminThreads: number
  internalThreads?: number
  directThreads?: number
  announcements?: number
}

// ========================
// Recipient/Directory Models
// ========================
export interface FamilyMemberModel {
  memberId: string
  name: string
  avatar: string | null
  relationship: string
  messagingEnabled: boolean
}

export interface StudentInfoModel {
  studentId: string
  name: string
  groupName: string | null
}

export interface FamilyRecipientModel {
  familyId: string
  familyName: string
  students: StudentInfoModel[]
  studentGroupIds: string[]
  studentStatuses: string[]
  members: FamilyMemberModel[]
}

export interface DirectoryGroupModel {
  groupId: string
  name: string
  familyCount: number
  memberCount: number
}

export interface FamilyDirectoryResponse {
  families: FamilyRecipientModel[]
  groups: DirectoryGroupModel[]
  totalMembers: number
}

export interface InternalGroupRecipientModel {
  groupId: string
  name: string
  staffCount: number
  conversationId: string | null
  isActive: boolean
}

export interface DirectAdminRecipientModel {
  id: string
  displayName: string
  email: string
  conversationId: string | null
  role: string | null
}

export interface AdminTeamRecipientModel {
  name: string
  conversationId: string | null
}

export interface InternalMessagingDirectoryModel {
  groups: InternalGroupRecipientModel[]
  canMessageAdminTeam: boolean
  adminTeam: AdminTeamRecipientModel | null
  myGroupIds: string[]
  myTemporaryGroupIds: string[]
  isAdmin: boolean
}

// ========================
// API Client Functions
// ========================

// Messages
export const sendMessage = async (
  payload: SendMessageRequest,
): Promise<MessageModel> => {
  const response = await request.post<MessageModel>(
    `${SERVICES.messaging}/${APIS.messages}`,
    payload,
  )
  return response.data
}

export const startDirectConversation = async (
  payload: StartDirectConversationRequest,
): Promise<MessageModel> => {
  const response = await request.post<MessageModel>(
    `${SERVICES.messaging}/${APIS.messages}/${APIS.direct}`,
    payload,
  )
  return response.data
}

export const sendBroadcastMessage = async (
  payload: SendBroadcastMessageRequest,
): Promise<BroadcastMessageModel> => {
  const response = await request.post<BroadcastMessageModel>(
    `${SERVICES.messaging}/${APIS.messages}/${APIS.broadcast}`,
    payload,
  )
  return response.data
}

export const getConversation = async (
  conversationId: string,
  pageSize?: number,
  continuationToken?: string,
): Promise<ConversationModel> => {
  const params: Record<string, any> = {}
  if (pageSize) params.pageSize = pageSize
  if (continuationToken) params.continuationToken = continuationToken

  const response = await request.get<ConversationModel>(
    `${SERVICES.messaging}/${APIS.conversations}/${conversationId}`,
    { params },
  )
  return response.data
}

export const searchMessages = async (
  conversationId: string,
  searchParams: SearchMessagesParams,
): Promise<ConversationModel> => {
  const params: Record<string, any> = {}
  if (searchParams.searchText) params.searchText = searchParams.searchText
  if (searchParams.fromDate)
    params.fromDate = searchParams.fromDate.toISOString()
  if (searchParams.toDate) params.toDate = searchParams.toDate.toISOString()
  if (searchParams.pageSize) params.pageSize = searchParams.pageSize
  if (searchParams.continuationToken)
    params.continuationToken = searchParams.continuationToken

  const response = await request.get<ConversationModel>(
    `${SERVICES.messaging}/${APIS.conversations}/${conversationId}/${APIS.search}`,
    { params },
  )
  return response.data
}

export const editMessage = async (
  conversationId: string,
  messageId: string,
  payload: EditMessageRequest,
): Promise<MessageModel> => {
  const response = await request.put<MessageModel>(
    `${SERVICES.messaging}/${APIS.conversations}/${conversationId}/${APIS.messages}/${messageId}`,
    payload,
  )
  return response.data
}

export const markMessageAsRead = async (
  conversationId: string,
  messageId: string,
): Promise<void> => {
  await request.put(
    `${SERVICES.messaging}/${APIS.conversations}/${conversationId}/${APIS.messages}/${messageId}/${APIS.read}`,
  )
}

export const markConversationAsUnread = async (
  conversationId: string,
): Promise<number> => {
  const response = await request.put<{ unreadCount: number }>(
    `${SERVICES.messaging}/${APIS.conversations}/${conversationId}/${APIS.unread}`,
  )
  return response.data.unreadCount
}

export const markMessageAsUnread = async (
  conversationId: string,
  messageId: string,
): Promise<number> => {
  const response = await request.put<{ unreadCount: number }>(
    `${SERVICES.messaging}/${APIS.conversations}/${conversationId}/${APIS.messages}/${messageId}/${APIS.unread}`,
  )
  return response.data.unreadCount
}

export const markAllAsRead = async (conversationId: string): Promise<void> => {
  await request.put(
    `${SERVICES.messaging}/${APIS.conversations}/${conversationId}/${APIS.readAll}`,
  )
}

export const getBroadcastMessage = async (
  broadcastMessageId: string,
): Promise<BroadcastMessageModel> => {
  const response = await request.get<BroadcastMessageModel>(
    `${SERVICES.messaging}/${APIS.broadcastMessages}/${broadcastMessageId}`,
  )
  return response.data
}

export const getBroadcastConversation = async (
  broadcastConversationId: string,
  take?: number,
): Promise<BroadcastConversationModel> => {
  const params: Record<string, any> = {}
  if (take) params.take = take
  const response = await request.get<BroadcastConversationModel>(
    `${SERVICES.messaging}/${APIS.broadcastConversations}/${broadcastConversationId}`,
    { params },
  )
  return response.data
}

// Inbox
export const getInbox = async (
  scope?: ConversationScope,
): Promise<InboxResponse> => {
  const params: Record<string, any> = {}
  if (scope) params.scope = scope

  const response = await request.get<InboxResponse>(
    `${SERVICES.messaging}/${APIS.inbox}`,
    { params },
  )
  return response.data
}

export const getUnreadCounts = async (): Promise<UnreadCountsResponse> => {
  const response = await request.get<UnreadCountsResponse>(
    `${SERVICES.messaging}/${APIS.inbox}/${APIS.unreadCounts}`,
  )
  return response.data
}

export const markAllAnnouncementsRead = async (): Promise<void> => {
  await request.put(
    `${SERVICES.messaging}/${APIS.inbox}/${APIS.announcements}/${APIS.readAll}`,
  )
}

export const acknowledgeUrgentConversation = async (
  conversationId: string,
  centerId?: string,
): Promise<void> => {
  const params: Record<string, string> = {}
  if (centerId) params.centerId = centerId
  await request.put(
    `${SERVICES.messaging}/${APIS.inbox}/${conversationId}/${APIS.acknowledgeUrgent}`,
    undefined,
    { params },
  )
}

// Announcements
export const sendAnnouncement = async (
  payload: SendAnnouncementRequest,
): Promise<AnnouncementModel> => {
  const response = await request.post<AnnouncementModel>(
    `${SERVICES.messaging}/${APIS.announcements}`,
    payload,
  )
  return response.data
}

export const getAnnouncements = async (
  pageSize?: number,
  continuationToken?: string,
  view?: 'outbox',
  outboxFilter?: 'mine' | 'client',
): Promise<AnnouncementsResponse> => {
  const params: Record<string, any> = {}
  if (pageSize) params.pageSize = pageSize
  if (continuationToken) params.continuationToken = continuationToken
  if (view) params.view = view
  if (outboxFilter) params.outboxFilter = outboxFilter

  const response = await request.get<AnnouncementsResponse>(
    `${SERVICES.messaging}/${APIS.announcements}`,
    { params },
  )
  return response.data
}

export const deleteAnnouncement = async (
  announcementId: string,
): Promise<void> => {
  await request.delete(
    `${SERVICES.messaging}/${APIS.announcements}/${announcementId}`,
  )
}

export const editAnnouncement = async (
  announcementId: string,
  payload: EditAnnouncementRequest,
): Promise<AnnouncementModel> => {
  const response = await request.put<AnnouncementModel>(
    `${SERVICES.messaging}/${APIS.announcements}/${announcementId}`,
    payload,
  )
  return response.data
}

// Directory
export const getFamilyDirectory = async (
  groupIds?: string[],
  studentStatuses?: string[],
): Promise<FamilyDirectoryResponse> => {
  const params: Record<string, any> = {}
  if (groupIds?.length) params.groupIds = groupIds.join(',')
  if (studentStatuses?.length)
    params.studentStatuses = studentStatuses.join(',')

  const response = await request.get<FamilyDirectoryResponse>(
    `${SERVICES.messaging}/${APIS.directory}/${APIS.families}`,
    { params },
  )
  return response.data
}

export const getInternalDirectory = async (
  actingGroupId: string,
): Promise<InternalMessagingDirectoryModel> => {
  const response = await request.get<InternalMessagingDirectoryModel>(
    `${SERVICES.messaging}/${APIS.directory}/${APIS.internal}/${actingGroupId}`,
  )
  return response.data
}

export const getAdminDirectory = async (): Promise<
  DirectAdminRecipientModel[]
> => {
  const response = await request.get<DirectAdminRecipientModel[]>(
    `${SERVICES.messaging}/${APIS.directory}/${APIS.admins}`,
  )
  return response.data
}

export const getGroups = async (): Promise<GroupModel[]> => {
  const response = await request.get<GroupModel[]>(
    `${SERVICES.messaging}/${APIS.directory}/${APIS.groups}`,
  )
  return response.data
}
